const HTML_TAG_REGEX = /<[^>]*>/gi;
const ZERO_WIDTH_REGEX = /[\u200B-\u200D\uFEFF]/g;
const UNICODE_SPACE_REGEX = /[\u00A0\u202F\u2007]/g;

const NAMED_HTML_ENTITIES: Record<string, string> = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  ensp: " ",
  emsp: " ",
  thinsp: " ",
};

function decodeHtmlEntities(text: string): string {
  return text.replace(/&(#x[0-9a-fA-F]+|#\d+|\w+);/g, (match, entity: string) => {
    if (entity.startsWith("#x")) {
      const codePoint = parseInt(entity.slice(2), 16);
      return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
    }

    if (entity.startsWith("#")) {
      const codePoint = parseInt(entity.slice(1), 10);
      return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
    }

    return NAMED_HTML_ENTITIES[entity.toLowerCase()] ?? match;
  });
}

function stripHtmlTags(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/div>\s*<div[^>]*>/gi, "\n\n")
    .replace(/<\/div>/gi, "\n\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<\/h[1-6]>/gi, "\n\n")
    .replace(HTML_TAG_REGEX, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n");
}

function normalizePlainText(raw: string): string {
  let text = decodeHtmlEntities(raw);
  text = text.replace(UNICODE_SPACE_REGEX, " ");
  text = text.replace(ZERO_WIDTH_REGEX, "");
  text = text.replace(/&/g, " e ");
  return text.replace(/\r\n/g, "\n");
}

/**
 * Cleans plain/markdown text without interpreting markdown syntax as HTML.
 */
export function sanitizePlainTextContent(raw: string): string {
  if (!raw) return "";
  return normalizePlainText(raw).trim();
}

/**
 * Converts rich HTML clipboard content to plain text for property descriptions.
 */
export function sanitizePropertyPlainText(raw: string): string {
  if (!raw) return "";
  return sanitizePlainTextContent(stripHtmlTags(raw));
}

export function sanitizePropertyDescriptionField(
  value: unknown
): string | undefined {
  if (value == null) return undefined;

  const sanitized = sanitizePlainTextContent(String(value));
  return sanitized === "" ? undefined : sanitized;
}

/**
 * Clipboard payloads from AI tools usually carry paragraph breaks in text/plain
 * and a flattened HTML block. Prefer plain text so markdown and line breaks survive.
 */
export function sanitizePropertyDescriptionPaste(
  plain: string,
  html: string
): string {
  if (plain.trim()) {
    return sanitizePlainTextContent(plain);
  }

  if (html.trim()) {
    return sanitizePropertyPlainText(html);
  }

  return "";
}
